/** Runtime validation of project files (untrusted input). */
import { z } from 'zod';

const id = z.string().min(1);
const point = z.object({ x: z.number(), y: z.number() });
const blanking = z.enum(['ok', 'missing']);
const propValue = z.union([z.string(), z.number(), z.boolean()]);

const wireEnd = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('port'), elementId: id, portId: z.string() }),
  z.object({ kind: z.literal('point'), x: z.number(), y: z.number() }),
]);

const party = {
  name: z.string(),
  street: z.string(),
  postcode: z.string(),
  city: z.string(),
  country: z.string(),
  vatNumber: z.string(),
  peppolId: z.string(),
  email: z.string(),
};

const invoice = z.object({
  id,
  kind: z.enum(['invoice', 'credit-note']).optional(),
  creditOf: z.object({ number: z.string(), issueDate: z.string() }).optional(),
  deposit: z.boolean().optional(),
  deducts: z.array(z.object({ number: z.string(), issueDate: z.string() })).optional(),
  billit: z.object({ orderId: z.number(), at: z.string(), peppol: z.boolean() }).optional(),
  number: z.string(),
  issueDate: z.string(),
  dueDate: z.string(),
  seller: z.object({ ...party, iban: z.string(), bic: z.string() }),
  buyer: z.object(party),
  lines: z.array(
    z.object({
      description: z.string(),
      reference: z.string(),
      quantity: z.number(),
      unitCode: z.string(),
      unitPrice: z.number(),
      vat: z.enum(['S21', 'S6', 'AE']).optional(),
    }),
  ),
  vat: z.enum(['S21', 'S6', 'AE']),
  paymentRef: z.string(),
  orderRef: z.string(),
  note: z.string(),
});

export const projectSchema = z.object({
  formatVersion: z.number().int(),
  id,
  meta: z.object({
    title: z.string(),
    ownerName: z.string(),
    ownerAddress: z.string(),
    installationAddress: z.string(),
    author: z.string(),
    date: z.string(),
    earthingSystem: z.enum(['TT', 'TN-S', 'TN-C-S', 'IT']),
    supplyVoltage: z.string(),
    company: z.string(),
    locale: z.enum(['fr', 'en', 'nl', 'de']),
    standard: z.enum(['rgie', 'nfc15100', 'nen1010']),
    iccOriginKa: z.number().positive().optional(),
    usage: z.enum(['domestic', 'non-domestic']).optional(),
    ownerEmail: z.string().optional(),
    ownerPhone: z.string().optional(),
  }),
  sheets: z.record(
    id,
    z.object({
      id,
      name: z.string(),
      kind: z.enum(['single-line', 'multi-line', 'control', 'position-plan', 'board']),
      paper: z.enum(['A4', 'A3']),
      orientation: z.enum(['landscape', 'portrait']),
      order: z.number(),
      background: z
        .object({
          assetId: z.string(),
          mime: z.string(),
          x: z.number(),
          y: z.number(),
          width: z.number().positive(),
          height: z.number().positive(),
          opacity: z.number().min(0).max(1),
        })
        .optional(),
      planScale: z.number().positive().optional(),
      source: z
        .object({
          kind: z.enum(['multi-line', 'board-layout', 'survey']),
          fromSheetId: z.string(),
          signature: z.string(),
          part: z.number().int().positive().optional(),
          level: z.string().optional(),
        })
        .optional(),
      blankingPlates: blanking.optional(),
    }),
  ),
  elements: z.record(
    id,
    z.object({
      id,
      sheetId: id,
      symbolId: z.string(),
      x: z.number(),
      y: z.number(),
      rotation: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]),
      mirrored: z.boolean(),
      tag: z.string(),
      circuitId: id.optional(),
      props: z.record(z.string(), propValue),
      photos: z.array(z.string()).optional(),
    }),
  ),
  wires: z.record(
    id,
    z.object({
      id,
      sheetId: id,
      from: wireEnd,
      to: wireEnd,
      via: z.array(point),
      label: z.string().optional(),
    }),
  ),
  circuits: z.record(
    id,
    z.object({
      id,
      code: z.string(),
      label: z.string(),
      cable: z.object({
        type: z.string(),
        sectionMm2: z.number(),
        cores: z.number().int(),
        lengthM: z.number().optional(),
        /** The length was estimated from the position plan, not measured. */
        lengthEstimated: z.boolean().optional(),
        installMethod: z.string().optional(),
      }),
      legacy: z.boolean(),
      spare: z.boolean().optional(),
    }),
  ),
  revisions: z.record(
    id,
    z.object({
      id,
      index: z.string(),
      date: z.string(),
      author: z.string(),
      description: z.string(),
      // The snapshot is a previous state of this same schema; checked loosely to keep loading fast.
      snapshot: z.record(z.string(), z.unknown()),
    }),
  ),
  symbols: z.record(
    id,
    z.object({
      id: z.string().regex(/^user\./),
      name: z.object({ fr: z.string(), en: z.string(), nl: z.string().optional() }),
      category: z.string(),
      views: z.array(z.string()),
      width: z.number().positive(),
      height: z.number().positive(),
      paths: z.array(z.string()),
      fills: z.array(z.string()).optional(),
      dashed: z.array(z.string()).optional(),
      texts: z
        .array(z.object({ text: z.string(), x: z.number(), y: z.number(), size: z.number() }))
        .optional(),
      ports: z.array(
        z.object({
          id: z.string(),
          x: z.number(),
          y: z.number(),
          dir: z.enum(['up', 'down', 'left', 'right']),
        }),
      ),
      tagPrefix: z.string(),
      label: z.array(z.string()).optional(),
      shapes: z.array(z.record(z.string(), z.unknown())).optional(),
    }),
  ),
  surveyRooms: z.record(
    id,
    z.object({
      id,
      name: z.string(),
      roomType: z.string(),
      level: z.string(),
      order: z.number(),
      notes: z.string(),
      photos: z.array(z.string()),
    }),
  ),
  surveyPoints: z.record(
    id,
    z.object({
      id,
      roomId: id,
      symbolId: z.string(),
      quantity: z.number().int().min(1),
      props: z.record(z.string(), propValue),
      circuitId: id.optional(),
      order: z.number(),
      notes: z.string(),
      photos: z.array(z.string()).optional(),
    }),
  ),
  surveyBoards: z.record(
    id,
    z.object({
      id,
      name: z.string(),
      order: z.number(),
      photos: z.array(z.string()),
      main: z.record(z.string(), propValue).optional(),
      mainRcd: z.record(z.string(), propValue).optional(),
      rcds: z.array(
        z.object({
          key: z.string(),
          sensitivityMa: z.number().positive(),
          rcdType: z.enum(['AC', 'A', 'F', 'B']),
          ratingA: z.number().positive(),
        }),
      ),
      earthOhm: z.number().nonnegative().optional(),
      blankingPlates: blanking.optional(),
      notes: z.string(),
    }),
  ),
  surveyCircuits: z.record(
    id,
    z.object({
      id,
      boardId: id,
      rcdKey: z.string().optional(),
      order: z.number(),
      label: z.string(),
      device: z.enum(['MCB', 'RCBO', 'fuse']),
      ratingA: z.number().positive().optional(),
      curve: z.enum(['B', 'C', 'D']).optional(),
      poles: z.number().int().positive().optional(),
      cable: z
        .object({
          type: z.string(),
          sectionMm2: z.number().positive(),
          cores: z.number().int().positive(),
          lengthM: z.number().positive().optional(),
        })
        .optional(),
      legacy: z.boolean(),
      notes: z.string(),
    }),
  ),
  measurements: z.record(
    id,
    z.object({
      id,
      target: z.enum(['circuit', 'rcd']),
      ref: z.string(),
      date: z.string(),
      testVoltageV: z.number().positive().optional(),
      insulationMOhm: z.number().nonnegative().optional(),
      continuityOhm: z.number().nonnegative().optional(),
      loopOhm: z.number().nonnegative().optional(),
      tripMs: z.number().nonnegative().optional(),
      tripMa: z.number().nonnegative().optional(),
      testButtonOk: z.boolean().optional(),
      retestOf: z.string().optional(),
      notes: z.string(),
    }),
  ),
  inspections: z.record(
    id,
    z.object({
      id,
      date: z.string(),
      body: z.string(),
      reference: z.string(),
      inspector: z.string(),
      result: z.enum(['conform', 'not-conform', 'unknown']),
      deadline: z.string().optional(),
      pages: z.array(z.string()),
      notes: z.string(),
    }),
  ),
  remarks: z.record(
    id,
    z.object({
      id,
      inspectionId: id,
      order: z.number(),
      text: z.string(),
      article: z.string(),
      ref: z.string(),
      status: z.enum(['open', 'fixed']),
      fixedOn: z.string().optional(),
      fixNote: z.string(),
      photos: z.array(z.string()),
      pin: z.object({ sheetId: id, x: z.number(), y: z.number() }).optional(),
      author: z.string().optional(),
    }),
  ),
  invoices: z.record(id, invoice),
  signoffs: z.record(
    id,
    z.object({
      id,
      kind: z.enum(['survey', 'handover']),
      at: z.string(),
      name: z.string(),
      signature: z.string().min(1),
      revision: z.string().optional(),
      note: z.string(),
    }),
  ),
});
